<template>
    <section
        class="relative w-screen -mx-[calc(50vw-50%)] bg-zinc-50 text-zinc-900 dark:bg-black dark:text-white overflow-hidden">
        <div class="absolute inset-0 pointer-events-none">
            <div class="absolute inset-0 transition-colors duration-500
               bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.12),transparent_60%)]
               dark:bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.18),transparent_60%)]"></div>
        </div>
        <div class="relative mx-auto w-[90%] max-w-6xl px-6 lg:px-10 pt-20 pb-16">
            <header class="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div>
                    <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
                        NexGrid · Color AI
                    </p>
                    <h2 class="mt-2 text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[0.95] tracking-tight">
                        Generate palettes with AI
                    </h2>
                    <p class="mt-3 max-w-xl text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-400">
                        Generate full semantic palettes or ask deep questions about contrast, branding and
                        accessibility. All inside NexGrid Studio.
                    </p>
                </div>

                <div
                    class="inline-flex items-center gap-2 self-start rounded-2xl border border-zinc-200/70 bg-white/80 px-3 py-2 text-[11px] text-zinc-600 shadow-sm backdrop-blur md:self-auto dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-300">
                    <span class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/90">
                        <span class="material-symbols-rounded text-[14px] text-zinc-950">bolt</span>
                    </span>
                    <span>With the best training technologies, optimized for color work.</span>
                </div>
            </header>

            <div class="mt-10 grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1.1fr)] items-stretch">
                <div
                    class="relative flex flex-col rounded-[26px] border border-zinc-200/80 bg-white/85 p-4 sm:p-5 shadow-[0_20px_60px_rgba(15,23,42,0.25)] backdrop-blur-xl dark:border-zinc-800/80 dark:bg-zinc-950/80">
                    <div class="flex items-center justify-between gap-3 mb-4">
                        <div>
                            <p class="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-400">
                                Assistants
                            </p>
                            <p class="mt-1 text-sm text-zinc-700 dark:text-zinc-300">
                                Pick how you want to think about colors.
                            </p>
                        </div>
                        <div
                            class="hidden sm:flex items-center gap-1 rounded-full bg-zinc-900 text-[11px] text-zinc-100 px-2.5 py-1 dark:bg-zinc-100 dark:text-zinc-900">
                            <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                            <span>Live in Studio</span>
                        </div>
                    </div>

                    <div
                        class="inline-flex items-center gap-1 rounded-2xl bg-zinc-100/80 p-1 text-[11px] text-zinc-600 dark:bg-zinc-900/80 dark:text-zinc-300">
                        <button v-for="a in assistants" :key="a.id" type="button" @click="activeId = a.id"
                            class="relative flex-1 rounded-2xl px-3 py-1.5 text-left transition-all duration-200"
                            :class="activeId === a.id
                                ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-50 dark:text-zinc-900'
                                : 'hover:bg-white/70 hover:text-zinc-900 dark:hover:bg-zinc-800/70'
                                ">
                            <div class="flex items-center gap-1.5">
                                <span
                                    class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-zinc-900/90 text-zinc-50 dark:bg-zinc-50 dark:text-zinc-900">
                                    <span class="material-symbols-rounded text-[13px]">
                                        {{ a.icon }}
                                    </span>
                                </span>
                                <span class="font-medium text-[11px] tracking-[0.14em] uppercase">
                                    {{ a.label }}
                                </span>
                            </div>
                        </button>
                    </div>

                    <div class="mt-5 space-y-4">
                        <div class="flex items-center justify-between gap-3">
                            <div>
                                <h3 class="text-sm font-semibold">
                                    {{ activeAssistant.title }}
                                </h3>
                                <p class="mt-1 text-[12px] leading-relaxed text-zinc-600 dark:text-zinc-400">
                                    {{ activeAssistant.subtitle }}
                                </p>
                            </div>
                            <div class="hidden sm:flex items-center gap-1.5 text-[11px] text-zinc-500">
                                <span class="h-1.5 w-1.5 rounded-full bg-sky-400/80"></span>
                                <span>{{ activeAssistant.metaHint }}</span>
                            </div>
                        </div>

                        <dl class="grid gap-3 sm:grid-cols-2 text-[11px] text-zinc-500 dark:text-zinc-400">
                            <div v-for="point in activeAssistant.points" :key="point.label"
                                class="rounded-2xl border border-zinc-200/70 bg-zinc-50/70 px-3 py-2.5 dark:border-zinc-800/80 dark:bg-zinc-900/80">
                                <dt
                                    class="flex items-center gap-1.5 text-[11px] font-medium text-zinc-700 dark:text-zinc-200">
                                    <span class="h-1.5 w-1.5 rounded-full bg-emerald-400/90"></span>
                                    {{ point.label }}
                                </dt>
                                <dd class="mt-1 text-[11px] leading-relaxed">
                                    {{ point.desc }}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div class="mt-5 flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
                        <span v-for="tag in activeAssistant.tags" :key="tag"
                            class="rounded-full bg-zinc-900/5 px-2 py-1 dark:bg-zinc-50/5">
                            {{ tag }}
                        </span>
                    </div>
                </div>

                <div
                    class="relative rounded-[28px] border border-zinc-200/80 bg-white/90 p-4 sm:p-5 shadow-[0_22px_70px_rgba(15,23,42,0.4)] backdrop-blur-xl dark:border-zinc-800/80 dark:bg-zinc-950/90">
                    <div class="flex items-center justify-between gap-3 mb-4">
                        <div class="flex items-center gap-2">
                            <div
                                class="flex h-8 w-8 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-400 text-zinc-950 shadow-[0_10px_30px_rgba(56,189,248,0.6)]">
                                <span class="material-symbols-rounded text-[18px]">palette</span>
                            </div>
                            <div>
                                <p class="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
                                    Live preview
                                </p>
                                <p class="text-[11px] text-zinc-400 dark:text-zinc-500">
                                    How the AI actually responds
                                </p>
                            </div>
                        </div>

                        <div
                            class="hidden sm:flex items-center gap-1 rounded-full bg-zinc-900/90 px-2.5 py-1 text-[10px] text-zinc-100 dark:bg-zinc-100 dark:text-zinc-900">
                            <span>{{ activeAssistant.badge }}</span>
                        </div>
                    </div>

                    <div class="flex items-center gap-2 mb-3 text-[11px]">
                        <span
                            class="inline-flex items-center gap-1 rounded-full bg-zinc-900 text-zinc-50 px-2 py-0.5 dark:bg-zinc-50 dark:text-zinc-900">
                            <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                            <span>Prompt</span>
                        </span>
                        <button v-for="ex in activeAssistant.examples" :key="ex.id" type="button"
                            @click="activeExampleId = ex.id"
                            class="rounded-full border px-2.5 py-1 text-[10px] transition-all" :class="activeExampleId === ex.id
                                ? 'border-zinc-900 bg-zinc-900 text-zinc-50 dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-900'
                                : 'border-zinc-200 bg-zinc-50 text-zinc-600 hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-zinc-700'
                                ">
                            {{ ex.label }}
                        </button>
                    </div>

                    <div
                        class="rounded-2xl border border-zinc-200/80 bg-zinc-50/80 p-3 text-[11px] text-zinc-700 shadow-inner dark:border-zinc-800/80 dark:bg-zinc-950/80 dark:text-zinc-200">
                        <p class="font-medium text-zinc-600 dark:text-zinc-200 mb-1">
                            You
                        </p>
                        <p class="leading-relaxed">
                            {{ activeExample.prompt }}
                        </p>
                    </div>

                    <div class="mt-3 flex items-center gap-1 text-[11px] text-zinc-500">
                        <span
                            class="inline-flex items-center gap-1 rounded-full bg-zinc-900 text-zinc-50 px-2 py-0.5 dark:bg-zinc-50 dark:text-zinc-900">
                            <span class="material-symbols-rounded text-[13px]">auto_awesome</span>
                            <span>AI answer</span>
                        </span>
                        <span>Structured and ready to use</span>
                    </div>

                    <div
                        class="mt-2 space-y-2 rounded-2xl border border-zinc-200/80 bg-white/95 p-3 text-[11px] leading-relaxed shadow-sm dark:border-zinc-800/80 dark:bg-zinc-900/90 dark:text-zinc-100">
                        <p class="text-[11px] font-semibold text-zinc-900 dark:text-zinc-50">
                            {{ activeExample.answer.title }}
                        </p>
                        <p class="text-[11px] text-zinc-600 dark:text-zinc-300">
                            {{ activeExample.answer.intro }}
                        </p>
                        <hr class="border-zinc-200/80 dark:border-zinc-800/80 my-1.5" />
                        <p class="font-medium text-[11px] text-zinc-800 dark:text-zinc-100">
                            {{ activeExample.answer.blockLabel }}
                        </p>
                        <ul class="mt-1 list-disc space-y-0.5 pl-4 text-[11px] text-zinc-600 dark:text-zinc-300">
                            <li v-for="item in activeExample.answer.items" :key="item">
                                {{ item }}
                            </li>
                        </ul>
                        <hr class="border-zinc-200/80 dark:border-zinc-800/80 my-1.5" />
                        <p class="text-[11px] text-zinc-500 dark:text-zinc-400">
                            {{ activeExample.answer.footer }}
                        </p>
                    </div>

                    <div
                        class="mt-4 flex flex-wrap items-center justify-between gap-2 text-[10px] text-zinc-500 dark:text-zinc-400">
                        <div class="flex items-center gap-1.5">
                            <span class="h-1.5 w-1.5 rounded-full bg-sky-400/80"></span>
                            <span>Output ready for real palettes, tokens and exports</span>
                        </div>
                        <div class="flex items-center gap-1.5">
                            <span
                                class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-zinc-900/90 text-zinc-50 dark:bg-zinc-50 dark:text-zinc-900">
                                <span class="material-symbols-rounded text-[13px]">content_copy</span>
                            </span>
                            <span>Copy to JSON, CSS, Tailwind or SCSS</span>
                        </div>
                    </div>

                    <div aria-hidden="true"
                        class="pointer-events-none absolute -right-6 -bottom-6 h-28 w-28 rounded-full bg-gradient-to-br from-sky-400/60 via-emerald-400/40 to-violet-500/60 opacity-60 blur-2xl">
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, ref } from 'vue'

const assistants = [
    {
        id: 'generator',
        label: 'Palette Generator',
        icon: 'auto_fix_high',
        title: 'From one sentence to a full semantic palette.',
        subtitle:
            'Feed Color AI a single line like “neon fintech dashboard at night” and get surfaces, accents, text colors and borders ready to plug into your design system.',
        metaHint: 'Semantic palette builder',
        badge: 'JSON · CSS · Tailwind ready',
        tags: ['Semantic tokens', 'UI systems', 'Exports'],
        points: [
            {
                label: 'Semantic first',
                desc: 'Colors come mapped to roles like surface, accent and text instead of random swatches.'
            },
            {
                label: 'Production formats',
                desc: 'Export straight to JSON, CSS variables, SCSS maps or Tailwind config.'
            },
            {
                label: 'Consistent mood',
                desc: 'The model keeps hue and contrast coherent across the entire set of colors.'
            },
            {
                label: 'Context-aware',
                desc: 'Optimized prompts for dashboards, marketing sites, apps and dark UIs.'
            }
        ],
        examples: [
            {
                id: 'ex1',
                label: 'Neon fintech',
                prompt:
                    'Design a dark fintech dashboard palette with deep navy surfaces, neon teal accent and legible text.',
                answer: {
                    title: 'Neon fintech palette (dark UI)',
                    intro:
                        'Here is a semantic palette tuned for a dark fintech dashboard with neon teal highlights.',
                    blockLabel: 'Core roles',
                    items: [
                        'Surface: Deep navy base that keeps focus on data visualizations.',
                        'Accent: Neon teal accent tuned for strong focus without eye strain.',
                        'Text primary: Soft off-white for main text with balanced contrast on dark.',
                        'Text secondary: Muted blue-grey for labels, hints and supporting copy.',
                        'Border: Low-contrast outlines that structure the UI without clutter.'
                    ],
                    footer: 'You can export this set as design tokens or plug directly into your Tailwind theme.'
                }
            },
            {
                id: 'ex2',
                label: 'Product landing',
                prompt:
                    'Create a bright SaaS marketing palette with a clean white base, soft blues and a single bold accent.',
                answer: {
                    title: 'Clean SaaS marketing palette',
                    intro:
                        'A light, approachable palette optimized for landing pages, hero sections and pricing tables.',
                    blockLabel: 'Core roles',
                    items: [
                        'Surface: Soft white with a hint of blue to feel crisp on high-resolution displays.',
                        'Accent: Saturated sky blue for CTAs and key links, tuned for WCAG AA on white.',
                        'Accent secondary: Subtle lilac for gradients, badges and secondary emphasis.',
                        'Text primary: Neutral charcoal that stays neutral next to both blues and lilac.',
                        'Border: Light cool-grey dividers that work with cards and tables.'
                    ],
                    footer: 'Ideal for marketing layouts where clarity and trust are the main signals.'
                }
            }
        ]
    },
    {
        id: 'advisor',
        label: 'Color Advisor',
        icon: 'chat',
        title: 'Ask questions, get color decisions explained.',
        subtitle:
            'Color Advisor answers “why” questions: contrast ratios, rebrands, accessibility trade-offs and how palettes behave on different screens.',
        metaHint: 'Decision support',
        badge: 'Structured, WCAG-aware answers',
        tags: ['Accessibility', 'Branding', 'Critique'],
        points: [
            {
                label: 'Contrast-aware',
                desc: 'Thinks in contrast ratios and real-world readability, not just hex codes.'
            },
            {
                label: 'Brand safe',
                desc: 'Helps you evolve an existing brand palette instead of replacing it blindly.'
            },
            {
                label: 'Multi-language',
                desc: 'Understands questions in any language and answers in English by default.'
            },
            {
                label: 'Structured output',
                desc: 'Every answer comes as a breakdown: analysis, recommendations and summary.'
            }
        ],
        examples: [
            {
                id: 'ex1',
                label: 'Logo + dark mode',
                prompt:
                    'My logo is bright red (#F3123C). I want a dark mode UI that doesn’t feel aggressive. Which accent and background colors should I pair with it?',
                answer: {
                    title: 'Balancing a bright red logo in dark mode',
                    intro:
                        'Your logo is intense, so the UI should calm it down instead of competing with it.',
                    blockLabel: 'Recommendations',
                    items: [
                        'Use a deep blue-ink surface instead of pure black to keep the red from vibrating.',
                        'Pick a cool desaturated accent (teal or cyan) for actions so the logo remains the hero.',
                        'Reserve bright red only for brand marks and critical states (errors, destructive actions).',
                        'Keep text off-white with a slight warm tint so it feels softer next to the red.'
                    ],
                    footer: 'This keeps the red logo iconic while your interface stays calm and readable.'
                }
            },
            {
                id: 'ex2',
                label: 'Accessibility',
                prompt:
                    'I have a lavender background (#E5D9FF) and want a primary button color that is accessible and still feels playful. What would you suggest?',
                answer: {
                    title: 'Accessible buttons on lavender',
                    intro:
                        'Lavender is light and soft, so your primary needs weight without looking heavy.',
                    blockLabel: 'Recommendations',
                    items: [
                        'Use a rich violet or deep indigo accent that hits AA contrast on top of lavender.',
                        'Keep the button label in near-white or very light lavender for a friendly look.',
                        'Avoid pure black labels; a deep indigo text color harmonizes better with the base.',
                        'Test hover states with slightly brighter or more saturated violet instead of darker.'
                    ],
                    footer: 'You stay playful while meeting contrast targets for real users and devices.'
                }
            }
        ]
    }
]

const activeId = ref('generator')
const activeExampleId = ref(assistants[0].examples[0].id)

const activeAssistant = computed(() => {
    return assistants.find((item) => item.id === activeId.value) ?? assistants[0]
})

const activeExample = computed(() => {
    const assistant = activeAssistant.value
    const found = assistant.examples.find((e) => e.id === activeExampleId.value)
    return found ?? assistant.examples[0]
})
</script>

<style scoped>
.material-symbols-rounded {
    font-family: 'Material Symbols Rounded', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI',
        sans-serif;
    font-weight: normal;
    font-style: normal;
    font-size: 1em;
    line-height: 1;
    letter-spacing: normal;
    text-transform: none;
    display: inline-block;
    white-space: nowrap;
    word-wrap: normal;
    direction: ltr;
    -webkit-font-feature-settings: 'liga';
    -webkit-font-smoothing: antialiased;
}
</style>
